import { queryOptions, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  OrganizationFeatureFlagsModel,
  OrganizationFeatureFlagsResponse,
} from 'src/models/organizations.models'
import { getOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.network'
import { orgQueryKeys } from 'src/queries/queries.constants'
import { ExtendBaseQueryOptions } from 'src/queries/queries.utils'
import { BackendErrorPayload } from 'src/utils/error.utils'

interface OrganizationFeatureFlagsListModelOptions
  extends ExtendBaseQueryOptions<
    OrganizationFeatureFlagsResponse,
    OrganizationFeatureFlagsModel
  > {
  orgId: string | undefined
}

const selectOrganizationFeatureFlagsModel = (
  data: OrganizationFeatureFlagsResponse,
): OrganizationFeatureFlagsModel => new OrganizationFeatureFlagsModel(data)

export const organizationFeatureFlagsQueries = {
  detail: (orgId: string | undefined) =>
    queryOptions<
      OrganizationFeatureFlagsResponse,
      AxiosError<BackendErrorPayload<string>>,
      OrganizationFeatureFlagsModel
    >({
      queryKey: orgQueryKeys.featureFlags(orgId),
      queryFn: () => {
        // enabled: !!orgId gates the initial fetch, but refetch()/enabled
        // overrides can still reach queryFn without an orgId — guard instead of
        // asserting `as string` so we fail loudly rather than request
        // `organizations/undefined/featureflags`.
        if (!orgId) {
          throw new Error(
            'organizationFeatureFlagsQueries.detail requires an orgId',
          )
        }
        return getOrganizationFeatureFlags(orgId)
      },
      select: selectOrganizationFeatureFlagsModel,
      // wait for orgId to be defined before making the request
      enabled: !!orgId,
      // what is an appropriate stale time for the org feature flags?
      staleTime: 1000 * 60,
    }),
}

export const useQueryOrganizationFeatureFlags = ({
  orgId,
  enabled = true,
  ...rest
}: OrganizationFeatureFlagsListModelOptions) => {
  const query = useQuery({
    ...organizationFeatureFlagsQueries.detail(orgId),
    enabled: enabled && !!orgId,
    ...rest,
  })
  // Preserve the legacy `useExtendQuery` contract: consumers read the unwrapped
  // backend error payload (`error.response.data`), not the raw AxiosError.
  return { ...query, error: query.error?.response?.data }
}
